/**
 * Platform detection utility
 * Detects macOS, Windows, or non-desktop platforms
 * Uses native navigator API for SSR-safe detection
 */

/**
 * Detects the platform type synchronously using navigator
 * @returns {'macos' | 'windows' | 'non-desktop'}
 */
export function detectPlatform(): 'macos' | 'windows' | 'non-desktop' {
	if (typeof window === 'undefined') {
		return 'non-desktop';
	}

	const userAgent = window.navigator.userAgent.toLowerCase();
	const platform = window.navigator.platform?.toLowerCase() || '';

	// Check for Windows first (before macOS to avoid false positives)
	if (
		platform.includes('win') ||
		userAgent.includes('windows') ||
		userAgent.includes('win32') ||
		userAgent.includes('win64') ||
		userAgent.includes('wow64')
	) {
		return 'windows';
	}

	// Check for macOS (includes Mac, iPhone, iPad)
	if (
		platform.includes('mac') ||
		userAgent.includes('mac os') ||
		userAgent.includes('macintosh') ||
		userAgent.includes('iphone') ||
		userAgent.includes('ipad')
	) {
		return 'macos';
	}

	// Default to non-desktop for mobile/tablet/other platforms
	return 'non-desktop';
}

/**
 * Gets the keyboard shortcut display text based on platform
 * @returns {string | null} The shortcut text or null if not applicable
 */
export function getKeyboardShortcut(): string | null {
	const platform = detectPlatform();

	switch (platform) {
		case 'macos':
			return '⌘K';
		case 'windows':
			return 'Ctrl+K';
		case 'non-desktop':
			return null;
		default:
			return null;
	}
}
